import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../Pages/Navbar/JobSeekerNavbar';
import './Training.css';
import defaultImage from "./images/students.png"
import { FaMapMarkerAlt, FaInfoCircle, FaPhone, FaUser, FaStar } from "react-icons/fa";
import BaseUrl from '../../ApiServices/BaseUrl';
import Footer from '../../Footer/Footer';

function Training() {
  const [trainings, setTrainings] = useState([]);
  const [filteredTrainings, setFilteredTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    country: '',
    services: '',
    sort: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchTrainings();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [trainings, filters]);

  const fetchTrainings = async () => {
    try {
      const response = await fetch(`${BaseUrl}/trainings/all`);
      if (!response.ok) {
        throw new Error('Failed to fetch trainings');
      }
      const data = await response.json();
      console.log('Fetched trainings:', data); // Debug log
      setTrainings(data);
      setFilteredTrainings(data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...trainings];

    if (filters.country && filters.country !== 'Country') {
      filtered = filtered.filter(training => 
        training.location?.toLowerCase().includes(filters.country.toLowerCase())
      );
    }

    if (filters.services && filters.services !== 'Services') {
      filtered = filtered.filter(training => 
        training.our_services?.toLowerCase().includes(filters.services.toLowerCase()) ||
        training.training_title?.toLowerCase().includes(filters.services.toLowerCase()) ||
        training.description?.toLowerCase().includes(filters.services.toLowerCase())
      );
    }

    if (filters.sort && filters.sort !== 'Most Popular') {
      switch (filters.sort) {
        case 'Top Rated':
          filtered = filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
          break;
        case 'New':
          filtered = filtered.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
          break;
        default:
          break;
      }
    }

    setFilteredTrainings(filtered);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleViewDetails = (trainingId) => {
    navigate(`/jobseeker-individual-training?id=${trainingId}`);
  };

  // Improved image error handler
  const handleImageError = (e, imageType) => {
    console.log(`Image error for ${imageType}:`, e.target.src);
    e.target.src = defaultImage;
    e.target.onerror = null; // Prevent infinite loop
  };

  // Check if image URL is valid
  const isValidImageUrl = (url) => {
    return url && url !== 'null' && url !== 'undefined' && !url.includes('null');
  };

  // Get image source with validation
  const getImageSource = (url, imageType) => {
    if (isValidImageUrl(url)) {
      return url;
    }
    return defaultImage;
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="trng-container trng-training-container">
          <div className="text-center mt-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading trainings...</p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Navbar />
        <div className="trng-container trng-training-container">
          <div className="alert alert-danger text-center mt-5" role="alert">
            Error: {error}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="trng-container trng-training-container">
        {/* Top Section */}
        <h2 className="training-title-wrapper">
          <div className='Training-title' style={{marginTop: "20px"}}>
            <FaUser className="training-title-icon" />
            <span className="training-title-text">Training</span>
          </div>
        </h2>

        <div className="text-center mb-4 trng-training-header">
          <h2 className="trng-title" style={{marginTop: "20px"}}>
            <i className="fas fa-phone-alt trng-contact-icon"></i> 
            <span className="trng-title-text">The Best Domestic Workers Trainings</span>
          </h2>
          <p className="trng-subtitle">
            A lot of trainings are available to provide to Foreign Domestic Workers more experience 
            (caregiver certificate, CPR and first aid classes, childcare) and help employer to improve 
            the skills of their helpers (cooking, housekeeping, languages, driving..). We are listing 
            the best trainings for any helper.
          </p>
        </div>

        {/* Dropdowns */}
        <div className="trng-card trng-dropdown-card p-3 mx-auto mb-3">
          <div className="trng-dropdown-row">
            <div className="trng-custom-dropdown">
              <select 
                value={filters.country} 
                onChange={(e) => handleFilterChange('country', e.target.value)}
              >
                <option value="">Country</option>
                <option value="Hong Kong">Hong Kong</option>
                <option value="Singapore">Singapore</option>
                <option value="UAE">UAE</option>
              </select>
            </div>

            <div className="trng-custom-dropdown">
              <select 
                value={filters.services} 
                onChange={(e) => handleFilterChange('services', e.target.value)}
              >
                <option value="">Services</option>
                <option value="Caregiver Training">Caregiver Training</option>
                <option value="Online Training">Online Training</option>
                <option value="Housekeeping">Housekeeping</option>
              </select>
            </div>

            <div className="trng-custom-dropdown">
              <select 
                value={filters.sort} 
                onChange={(e) => handleFilterChange('sort', e.target.value)}
              >
                <option value="">Most Popular</option>
                <option value="Top Rated">Top Rated</option>
                <option value="New">New</option>
              </select>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="text-center mb-3">
          <p className="text-muted">
            Showing {filteredTrainings.length} of {trainings.length} trainings
          </p>
        </div>

        <div className="row justify-content-center">
          {filteredTrainings.length > 0 ? (
            filteredTrainings.map((training) => (
              <div key={training.id} className="col-lg-4 col-md-6 col-sm-12 mb-4">
                <div className="trng-card trng-service-card h-100 shadow-sm position-relative">
                  {/* Banner Image */}
                  <div className="position-relative">
                    <img 
                      src={getImageSource(training.back_profile, 'banner')}
                      alt={`${training.training_title} Banner`} 
                      className="trng-card-img-top trng-banner-img" 
                      onError={(e) => handleImageError(e, 'banner')}
                      loading="lazy"
                    />
                    
                    {/* Logo */}
                    <img 
                      src={getImageSource(training.profile_image, 'logo')}
                      alt={`${training.training_title} Logo`} 
                      className="rounded-circle trng-logo-img" 
                      onError={(e) => handleImageError(e, 'logo')}
                      loading="lazy"
                    />

                    {/* Rating */}
                    <div className="trng-rating-badge">
                      <FaStar className="trng-star-icon" />
                      <span>{training.rating || 'N/A'}</span>
                    </div>
                  </div>

                  <div className="trng-card-body">
                    {/* Location */}
                    <div className="mb-2">
                      <span className="trng-location-text">
                        <FaMapMarkerAlt className="trng-location-icon" /> 
                        {training.location || 'Location not specified'}
                      </span>
                    </div>

                    <h3 className="trng-tenderheart-home">{training.training_title}</h3>
                    <p className="trng-card-text text-black trng-description">
                      {training.description || 'No description available.'}
                    </p>

                    <h5 className="mt-4 trng-service-subheading text-blue text-start">
                      Our Services:
                    </h5>
                    <span className="trng-badge trng-training-badge">
                      {training.our_services || 'General Training'}
                    </span>

                    <div className="trng-card-buttons">
                      <button 
                        className="btn btn-success rounded-pill trng-contact-btn"
                        onClick={() => navigate('/jobseeker-contactus')}
                      >
                        <FaPhone /> CONTACT US
                      </button>
                      <button 
                        className="btn btn-warning rounded-pill trng-details-btn"
                        onClick={() => handleViewDetails(training.id)}
                      >
                        <FaInfoCircle /> MORE DETAILS
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-12 text-center">
              <div className="alert alert-info" role="alert">
                No trainings found matching your filters. Please try different criteria.
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer/>
    </>
  );
}

export default Training;